import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Building2,
  Briefcase,
  Users,
  CalendarCheck,
  Clock,
  Camera,
  Settings,
  LogOut,
  LayoutDashboard,
  AlarmClock,
  Maximize2,
  Minimize2,
  RefreshCw,
} from "lucide-react";
import Logo from "../components/Logo";

const navItems = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/clients", label: "Clients", icon: Building2 },
  { to: "/admin/projects", label: "Projects", icon: Briefcase },
  { to: "/admin/employees", label: "Employees", icon: Users },
  { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [density, setDensity] = useState(
    () => localStorage.getItem("itcot.density") || "compact"
  );
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(new Date());
  const [lastRefreshAt] = useState(() => new Date());

  // Live clock tick
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    if (density === "comfortable") html.classList.add("density-comfortable");
    else html.classList.remove("density-comfortable");
    localStorage.setItem("itcot.density", density);
  }, [density]);

  const doRefresh = () => {
    setRefreshing(true);
    setTimeout(() => window.location.reload(), 250);
  };

  // F5 / Ctrl+R / Cmd+R intercept
  useEffect(() => {
    const onKey = (e) => {
      const isF5 = e.key === "F5";
      const isReload =
        (e.ctrlKey || e.metaKey) && (e.key === "r" || e.key === "R");
      if (isF5 || isReload) {
        e.preventDefault();
        doRefresh();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = () => {
    logout();
    nav("/");
  };

  // IST formatted clock + "updated Xs ago"
  const clockTxt = now.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  const istDate = now.toLocaleDateString("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }); // dd/mm/yyyy → replace / with .
  const dateTxt = istDate.replaceAll("/", ".");
  const secondsAgo = Math.floor((now.getTime() - lastRefreshAt.getTime()) / 1000);
  const agoTxt =
    secondsAgo < 5
      ? "just now"
      : secondsAgo < 60
      ? `${secondsAgo}s ago`
      : secondsAgo < 3600
      ? `${Math.floor(secondsAgo / 60)}m ago`
      : `${Math.floor(secondsAgo / 3600)}h ago`;

  return (
    <div className="min-h-screen flex bg-[#F4F7F9]">
      {/* Sidebar — sticky so the "Signed in as" footer never gets pushed off-screen */}
      <aside className="w-60 bg-[#1A3A5C] text-white flex flex-col sticky top-0 h-screen self-start shrink-0">
        <div className="p-5 border-b border-white/10 flex justify-center shrink-0">
          <div className="bg-white rounded-lg p-2">
            <Logo size={40} showTagline={false} />
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? "bg-white text-[#1A3A5C] font-semibold"
                    : "text-white/85 hover:bg-white/10"
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 shrink-0 space-y-2">
          {/* Density toggle */}
          <div className="bg-white/10 rounded-lg p-1 flex" data-testid="density-toggle">
            <button
              data-testid="density-compact"
              onClick={() => setDensity("compact")}
              className={`flex-1 flex items-center justify-center gap-1 text-[11px] py-1.5 rounded-md transition-colors ${
                density === "compact"
                  ? "bg-white text-[#1A3A5C] font-semibold"
                  : "text-white/85 hover:bg-white/10"
              }`}
            >
              <Minimize2 className="w-3 h-3" /> Compact
            </button>
            <button
              data-testid="density-comfortable"
              onClick={() => setDensity("comfortable")}
              className={`flex-1 flex items-center justify-center gap-1 text-[11px] py-1.5 rounded-md transition-colors ${
                density === "comfortable"
                  ? "bg-white text-[#1A3A5C] font-semibold"
                  : "text-white/85 hover:bg-white/10"
              }`}
            >
              <Maximize2 className="w-3 h-3" /> Comfortable
            </button>
          </div>
          <div className="px-3 py-1 text-xs text-white/85 truncate whitespace-nowrap">
            Signed in as{" "}
            <span className="font-semibold text-white">
              {user?.username || "admin"}
            </span>
          </div>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 rounded-lg py-2 text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Sticky thin bar — live clock + last-refreshed badge + refresh action */}
        <div className="sticky top-0 z-30 bg-[#F4F7F9]/85 backdrop-blur-sm border-b border-slate-200/60 flex items-center justify-between gap-3 px-4 py-2">
          <div className="flex items-center gap-3 text-xs text-[#475569]">
            <span
              data-testid="live-clock"
              className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1 font-mono font-semibold text-[#1A3A5C] tabular-nums shadow-sm"
            >
              <Clock className="w-3.5 h-3.5 text-[#2A8A8F]" />
              {clockTxt}
              <span className="text-[#475569] font-normal ml-1">{dateTxt}</span>
              <span className="text-[10px] uppercase tracking-wider text-[#94a3b8] ml-1">IST</span>
            </span>
            <span className="text-[#94a3b8] hidden sm:inline" data-testid="last-refreshed">
              Updated {agoTxt}
            </span>
          </div>
          <button
            data-testid="page-refresh"
            onClick={doRefresh}
            disabled={refreshing}
            title="Refresh this page (F5)"
            className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A3A5C] hover:text-white border border-slate-200 text-[#1A3A5C] rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div className="flex-1 p-6 lg:p-8 pb-24 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
